import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import * as Sentry from "@sentry/react";

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'protected-route' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'protected-route' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'protected-route',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Security: Safe error messages mapping
const ERROR_MESSAGES = {
  // Network & Server Errors
  'NETWORK_ERROR': 'Network error. Please check your connection and try again.',
  'SERVER_ERROR': 'Service temporarily unavailable. Please try again later.',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
  
  // Authentication & Session Errors
  'AUTHENTICATION_FAILED': 'Authentication failed. Please try again.',
  'INVALID_CREDENTIALS': 'Invalid email or password. Please try again.',
  'SESSION_EXPIRED': 'Your session has expired. Please sign in again.',
  'SESSION_CHECK_ERROR': 'Session check failed. Please try again.',
  
  // Rate Limiting
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  
  // OAuth Errors
  'OAUTH_CALLBACK_FAILED': 'Social login failed',
  
  // Registration & Login
  'REGISTRATION_FAILED': 'Registration failed. Please try again.',
  'LOGIN_FAILED': 'Login failed. Please try again.',
  
  // OTP Errors
  'SEND_OTP_FAILED': 'Failed to send verification code. Please try again.',
  'VERIFY_OTP_FAILED': 'Invalid verification code. Please try again.',
  
  // Password Errors
  'FORGOT_PASSWORD_FAILED': 'Password reset request failed. Please try again.',
  'RESET_PASSWORD_FAILED': 'Password reset failed. Please try again.',
  
  // Validation Errors
  'VALIDATION_ERROR': 'Please check your input and try again.',
  'INVALID_REQUEST': 'Invalid request. Please try again.',
    'WRONG_PROVIDER': 'Please sign in using the method you originally registered with.',
  'USE_DIFFERENT_PROVIDER': 'Please use a different sign-in method.',
  // ===== ProtectedRoute-Specific Error Codes =====
  
  // Route Protection Errors
  'AUTH_ERROR': 'Please log in again to continue',
  'ACCESS_DENIED': 'Access denied. Please log in.',
  'INVALID_STATE': 'Invalid authentication state',
  'NAVIGATION_ERROR': 'Navigation error occurred',
  'INITIALIZATION_ERROR': 'Authentication initialization failed'
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// ✅ Already correct - matches AuthContext logic exactly
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return ERROR_MESSAGES['SERVER_ERROR'];
  }
  
  // ✅ CHECK INTERCEPTOR FIELDS FIRST!
  if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.error) {
    return error.error;
  }
  
  // ✅ Check backend error code first (in response data)
  if (error.response?.data?.code && ERROR_MESSAGES[error.response.data.code]) {
    return ERROR_MESSAGES[error.response.data.code];
  }
  
  // ✅ Check direct error code
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // ✅ Handle HTTP status codes
  if (error.response?.status === 401 || error.status === 401) {
    return ERROR_MESSAGES['SESSION_EXPIRED'];
  }
  
  // Fallback to sanitized message if available
  if (error.message && typeof error.message === 'string') {
    return sanitizeText(error.message);
  }
  
  return ERROR_MESSAGES['SERVER_ERROR'];
};

// Performance: Route resolution metrics
const RouteMetrics = {
  totalResolutions: 0,
  successfulResolutions: 0,
  failedResolutions: 0,
  averageResolutionTime: 0,
  securityEvents: 0,
  
  record(duration, success = true, isSecurityEvent = false) {
    this.totalResolutions++;
    if (success) {
      this.successfulResolutions++;
    } else {
      this.failedResolutions++;
    }
    
    if (isSecurityEvent) {
      this.securityEvents++;
    }
    
    // Update average resolution time
    this.averageResolutionTime = 
      (this.averageResolutionTime * (this.totalResolutions - 1) + duration) / 
      this.totalResolutions;
  },
  
  getMetrics() {
    return {
      ...this,
      successRate: this.totalResolutions > 0 ? 
        (this.successfulResolutions / this.totalResolutions * 100).toFixed(2) + '%' : 
        '0%'
    };
  }
};

// Security: URL validation and sanitization
const validateAndSanitizeUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return '/';
  }
  
  try {
    // Remove any potentially dangerous characters
    const sanitized = url.replace(/[<>'"]/g, '');
    
    // Ensure it's a relative path starting with /
    if (!sanitized.startsWith('/')) {
      return '/';
    }
    
    // Prevent path traversal
    if (sanitized.includes('..') || sanitized.includes('//')) {
      logError('Path traversal attempt detected', { originalUrl: url, sanitizedUrl: sanitized });
      return '/';
    }
    
    // Additional validation for known safe patterns
    const safePathRegex = /^\/[a-zA-Z0-9\-_\/]*(\?[a-zA-Z0-9\-_=&]*)?$/;
    if (!safePathRegex.test(sanitized)) {
      logError('Unsafe URL pattern detected', { originalUrl: url, sanitizedUrl: sanitized });
      return '/';
    }
    
    return sanitized;
    
  } catch (error) {
    logError('URL validation error', { originalUrl: url }, error);
    return '/';
  }
};

// Security: Enhanced error parameter detection and cleanup
const detectAndCleanErrorParams = () => {
  const startTime = performance.now();
  
  try {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const searchParams = new URLSearchParams(window.location.search);
    
    const errorSources = [
      { source: 'hash', params: hashParams },
      { source: 'search', params: searchParams }
    ];
    
    let hasSecurityIssue = false;
    const detectedErrors = [];
    
    errorSources.forEach(({ source, params }) => {
      // Check for authentication errors
      const error = params.get('error');
      const errorDescription = params.get('error_description');
      
      if (error) {
        detectedErrors.push({
          source,
          error: sanitizeText(error),
          description: sanitizeText(errorDescription)
        });
        
        // Check for security-related errors
        const securityErrors = ['access_denied', 'invalid_request', 'unauthorized_client', 'invalid_scope'];
        if (securityErrors.includes(error)) {
          hasSecurityIssue = true;
        }
      }
      
      // Check for login_required specifically
      if (error === 'login_required') {
        hasSecurityIssue = true;
      }
    });
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    logPerformance('detect_error_params', duration, {
      hasErrors: detectedErrors.length > 0,
      hasSecurityIssue,
      errorCount: detectedErrors.length
    });
    
    if (detectedErrors.length > 0) {
      logInfo('Error parameters detected', {
        errors: detectedErrors,
        hasSecurityIssue,
        duration: `${duration.toFixed(2)}ms`
      });
      
      if (hasSecurityIssue) {
        logUserAction('security_error_params_detected', {
          errors: detectedErrors,
          currentUrl: window.location.href
        });
      }
    }
    
    return {
      hasErrors: detectedErrors.length > 0,
      hasSecurityIssue,
      errors: detectedErrors,
      needsCleanup: detectedErrors.length > 0
    };
    
  } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    logPerformance('detect_error_params_failed', duration, {
      errorName: error.name
    });
    
    logError('Error parameter detection failed', {
      duration: `${duration.toFixed(2)}ms`
    }, error);
    
    return {
      hasErrors: false,
      hasSecurityIssue: false,
      errors: [],
      needsCleanup: false
    };
  }
};

// Performance: Navigation decision engine
const NavigationDecisionEngine = {
  analyze(authState, routeState, errorState) {
    const startTime = performance.now();
    
    try {
      const decision = {
        shouldNavigate: false,
        destination: null,
        reason: null,
        isSecurityRelated: false,
        requiresCleanup: false
      };
      
      // Handle error cleanup first
      if (errorState.needsCleanup) {
        decision.requiresCleanup = true;
      }
      
      // Handle security issues
      if (errorState.hasSecurityIssue) {
        decision.shouldNavigate = true;
        decision.destination = '/login';
        decision.reason = 'security_error_detected';
        decision.isSecurityRelated = true;
        
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        logPerformance('navigation_decision_security', duration, {
          reason: decision.reason,
          destination: decision.destination
        });
        
        return decision;
      }
      
      // Standard navigation logic
      if (routeState.isPublicRoute) {
        if (authState.isAuthenticated && routeState.isAuthPage) {
          decision.shouldNavigate = true;
          decision.destination = '/';
          decision.reason = 'authenticated_user_on_auth_page';
        }
      } else if (!authState.isAuthenticated) {
        decision.shouldNavigate = true;
        decision.destination = '/login';
        decision.reason = 'unauthenticated_user_on_protected_route';
      }
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('navigation_decision_standard', duration, {
        reason: decision.reason,
        destination: decision.destination,
        shouldNavigate: decision.shouldNavigate
      });
      
      return decision;
      
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('navigation_decision_error', duration, {
        errorName: error.name
      });
      
      logError('Navigation decision analysis failed', {
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      // Safe fallback
      return {
        shouldNavigate: false,
        destination: null,
        reason: 'analysis_failed',
        isSecurityRelated: false,
        requiresCleanup: false
      };
    }
  }
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, isInitialized, error } = useAuth();
  const location = useLocation();
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const [navigationDestination, setNavigationDestination] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Performance: Component lifecycle tracking
  const componentMountTime = useRef(Date.now());
  const lastNavigationTime = useRef(null);
  
  // Security: Track authentication state changes
  const previousAuthState = useRef({
    isAuthenticated: null,
    isLoading: null
  });

  // Define public routes with enhanced security validation
  const PUBLIC_ROUTES = useMemo(() => ['/', '/login', '/signup', '/callback'], []);
  
  // Set Sentry context for route protection
  useEffect(() => {
    Sentry.setContext("protected-route", {
      isAuthenticated,
      isLoading,
      isInitialized,
      pathname: location.pathname,
      isPublicRoute: PUBLIC_ROUTES.includes(location.pathname),
      componentAge: Date.now() - componentMountTime.current
    });
  }, [isAuthenticated, isLoading, isInitialized, location.pathname, PUBLIC_ROUTES]);

  // Component mount logging
  useEffect(() => {
    logInfo('ProtectedRoute component mounted', {
      pathname: location.pathname,
      isAuthenticated,
      isLoading,
      isInitialized,
      isPublicRoute: PUBLIC_ROUTES.includes(location.pathname)
    });
    
    logUserAction('protected_route_mounted', {
      pathname: location.pathname,
      isAuthenticated
    });
  }, []);

  // Enhanced navigation decision logic with comprehensive security checks
  const makeNavigationDecision = useCallback(() => {
    const startTime = performance.now();
    
    try {
      // Skip processing if still initializing/loading
      if (isLoading || !isInitialized) {
        logInfo('Skipping navigation decision - still loading/initializing', {
          pathname: location.pathname,
          isLoading,
          isInitialized
        });
        return;
      }
      
      setIsProcessing(true);
      
      // Detect and analyze error parameters
      const errorState = detectAndCleanErrorParams();
      
      // Analyze route state
      const routeState = {
        isPublicRoute: PUBLIC_ROUTES.includes(location.pathname),
        isAuthPage: ['/login', '/signup'].includes(location.pathname),
        isCallbackRoute: location.pathname === '/callback',
        pathname: location.pathname
      };
      
      // Analyze authentication state
      const authState = {
        isAuthenticated,
        isLoading,
        isInitialized,
        hasStateChanged: 
          previousAuthState.current.isAuthenticated !== isAuthenticated ||
          previousAuthState.current.isLoading !== isLoading
      };
      
      // Update previous auth state
      previousAuthState.current = {
        isAuthenticated,
        isLoading
      };
      
      // Make navigation decision
      const decision = NavigationDecisionEngine.analyze(authState, routeState, errorState);
      
      // Execute decision
      if (decision.requiresCleanup && (window.location.hash || window.location.search)) {
        logUserAction('url_cleanup_performed', {
          originalUrl: window.location.href,
          pathname: location.pathname,
          reason: decision.reason
        });
        
        window.history.replaceState(null, '', location.pathname);
      }
      
      if (decision.shouldNavigate) {
        setShouldNavigate(true);
        setNavigationDestination(validateAndSanitizeUrl(decision.destination));
        lastNavigationTime.current = Date.now();
        
        logUserAction('navigation_decision_made', {
          from: location.pathname,
          to: decision.destination,
          reason: decision.reason,
          isSecurityRelated: decision.isSecurityRelated
        });
        
        if (decision.isSecurityRelated) {
          logError('Security-related navigation triggered', {
            from: location.pathname,
            to: decision.destination,
            reason: decision.reason,
            errorState
          });
        }
      } else {
        setShouldNavigate(false);
        setNavigationDestination(null);
      }
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      RouteMetrics.record(duration, true, decision.isSecurityRelated);
      
      logPerformance('navigation_decision_complete', duration, {
        shouldNavigate: decision.shouldNavigate,
        destination: decision.destination,
        reason: decision.reason,
        isSecurityRelated: decision.isSecurityRelated
      });
      
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      RouteMetrics.record(duration, false, false);
      
      logPerformance('navigation_decision_failed', duration, {
        errorName: error.name
      });
      
      logError('Navigation decision failed', {
        pathname: location.pathname,
        isAuthenticated,
        isLoading,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      // Safe fallback - don't navigate on error
      setShouldNavigate(false);
      setNavigationDestination(null);
    } finally {
      setIsProcessing(false);
    }
  }, [isAuthenticated, isLoading, isInitialized, location.pathname, PUBLIC_ROUTES]);

  // Main navigation decision effect
  useEffect(() => {
    makeNavigationDecision();
  }, [makeNavigationDecision]);

  // Security: Monitor for rapid authentication state changes (potential attack)
  useEffect(() => {
    const now = Date.now();
    const timeSinceLastNavigation = lastNavigationTime.current ? 
      now - lastNavigationTime.current : null;
    
    if (timeSinceLastNavigation && timeSinceLastNavigation < 1000) { // Less than 1 second
      logError('Rapid authentication state changes detected', {
        timeSinceLastNavigation,
        pathname: location.pathname,
        isAuthenticated,
        isLoading
      });
      
      logUserAction('rapid_auth_state_change', {
        timeSinceLastNavigation,
        pathname: location.pathname,
        isAuthenticated
      });
    }
  }, [isAuthenticated, isLoading]);

  // Performance: Component cleanup and metrics reporting
  useEffect(() => {
    return () => {
      const componentLifetime = Date.now() - componentMountTime.current;
      
      logPerformance('protected_route_unmount', componentLifetime, {
        pathname: location.pathname,
        finalAuthState: isAuthenticated,
        routeMetrics: RouteMetrics.getMetrics()
      });
      
      logInfo('ProtectedRoute component unmounted', {
        pathname: location.pathname,
        lifetime: `${componentLifetime}ms`,
        isAuthenticated
      });
    };
  }, []);

  // Enhanced loading state with security considerations
  if (isLoading || !isInitialized || isProcessing) {
    logInfo('Showing loading state', {
      isLoading,
      isInitialized,
      isProcessing,
      pathname: location.pathname
    });
    
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-white text-sm">
            {isLoading ? 'Loading...' : !isInitialized ? 'Initializing...' : 'Processing...'}
          </p>
        </div>
      </div>
    );
  }

  // Special handling for callback route with enhanced security
  if (location.pathname === '/callback') {
    logInfo('Rendering callback route', {
      isAuthenticated,
      hasUrlParams: !!(window.location.search || window.location.hash)
    });
    
    logUserAction('callback_route_accessed', {
      isAuthenticated,
      hasUrlParams: !!(window.location.search || window.location.hash)
    });
    
    return children;
  }

  // Execute navigation with comprehensive logging
  if (shouldNavigate && navigationDestination) {
    const sanitizedDestination = validateAndSanitizeUrl(navigationDestination);
    
    logUserAction('navigation_executed', {
      from: location.pathname,
      to: sanitizedDestination,
      isAuthenticated,
      navigationAge: lastNavigationTime.current ? 
        Date.now() - lastNavigationTime.current : null
    });
    
    if (isAuthenticated) {
      logInfo('Authenticated user navigation', {
        from: location.pathname,
        to: sanitizedDestination
      });
      
      return <Navigate to={sanitizedDestination} replace />;
    } else {
      const wasSessionExpired = error?.code === 'SESSION_EXPIRED';
      
      logInfo('Unauthenticated user navigation', {
        from: location.pathname,
        to: sanitizedDestination,
        returnUrl: location.pathname,
        hasSessionExpiredError: wasSessionExpired
      });
      
      return (
        <Navigate 
          to={sanitizedDestination} 
          state={{ 
            from: location.pathname,
            message: wasSessionExpired ? 'Due to inactivity, your session has expired' : undefined
          }}
          replace 
        />
      );
    }
  }

  // Log successful route access
  logInfo('Route access granted', {
    pathname: location.pathname,
    isAuthenticated,
    isPublicRoute: PUBLIC_ROUTES.includes(location.pathname)
  });

  return children;
};

export default ProtectedRoute;